import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { fetchCompanyMoneyMindUserDetail } from './companyMoneyMindApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_COLORS,
  toneChip
} from '../../../../component/common/designSystem';

/**
 * MoneyMind → User profile.
 *
 * How much this person has logged, over how many separate days, how they sorted
 * it and when they last came back.
 *
 * NOT ONE FIGURE ON THIS PAGE IS A SUM OF MONEY, and there is no way to get one
 * from the endpoint behind it. What was bought and the reflective note attached
 * to it are never read either. Somebody's shopping is not their employer's
 * business, and the activity view is deliberately month-by-month rather than
 * entry-by-entry: a list of purchases with a name at the top of it is a
 * spending diary, not a wellbeing metric.
 *
 * `fetcher`/`backPath`/`backLabel` are props so a super-admin MoneyMind hub can
 * reuse the page against the unscoped endpoint.
 */
const MoneyMindUserDetail = ({
  fetcher = fetchCompanyMoneyMindUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const tagMix = detail?.tagMix || [];
  const currencyMix = detail?.currencyMix || [];
  const monthlyActivity = detail?.monthlyActivity || [];

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.logs} title="Things Logged" value={user ? user.entries : '-'} />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Logged"
          value={user ? user.activeDays : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Entries per Day"
          value={user ? user.avgPerActiveDay : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Added a Note"
          value={user?.entries > 0 ? `${user.noteRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="First Entry"
          value={user?.firstEntryDate || '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Last Entry"
          value={user?.lastEntryDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="How They Sort It" subtitle="Keep, unsure, or let it go">
          {user?.entries > 0 ? (
            <ProgressList items={tagMix} unit="entries" color={CHART_COLORS.brand} />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>

        {/* A count of entries per currency. Not a total in any of them. */}
        <Panel title="Currencies Used" subtitle="A count of entries, not a total">
          {currencyMix.length > 0 ? (
            <ProgressList items={currencyMix} unit="entries" color={CHART_COLORS.teal} />
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Usage Summary" subtitle="The raw counts for this person">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Entries logged</td>
              <td className="py-2 text-right">{user ? user.entries : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Separate days logged</td>
              <td className="py-2 text-right">{user ? user.activeDays : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Entries with a note</td>
              <td className="py-2 text-right">{user ? user.entriesWithNote : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First entry</td>
              <td className="py-2 text-right">{user?.firstEntryDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last entry</td>
              <td className="py-2 text-right">{user?.lastEntryDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      {/* Deliberately a month and a count, not a list of entries. */}
      <div className="mt-6">
        <Panel
          title="Activity by Month"
          subtitle="How often they come back — individual entries are not shown"
        >
          {monthlyActivity.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 px-4 font-medium text-center">Entries</th>
                  <th className="py-2 font-medium text-right">Days Logged</th>
                </>
              }
            >
              {monthlyActivity.map((row) => (
                <tr key={row.month} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.month}</td>
                  <td className="py-2 px-4 text-center">{row.entries}</td>
                  <td className="py-2 text-right">{row.activeDays}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>Nothing logged yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

MoneyMindUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

MoneyMindUserDetail.defaultProps = {
  backLabel: 'Back to Employees'
};

export default MoneyMindUserDetail;
